import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  Heading,
  Input,
  Spinner,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { FaComment } from "react-icons/fa";
import { CommercialClient, SHARED_INTRANET_FIELDS } from "../../models/CommercialClient";
import { companyTypeLabel } from "../../models/Company";
import { IntranetClientFields } from "../../models/Intranet";
import { addIntranetClientComment, getIntranetClient, updateIntranetClient } from "../../services/intranetService";
import { crm } from "../../theme";
import ProceduresPanel from "./ProceduresPanel";

interface Props {
  clientId: string | null;
  onClose: () => void;
  /** Recarrega a galeria depois de salvar. */
  onSaved: () => void;
}

const FIELDS: { key: keyof IntranetClientFields; label: string; placeholder?: string }[] = [
  { key: "modality", label: "Modalidade", placeholder: "Conta e Ordem, Encomenda..." },
  { key: "analyst", label: "Analista" },
  { key: "internationalFreight", label: "Contratação de frete marítimo", placeholder: "Cliente / CVF" },
  { key: "followUpFrequency", label: "Follow-up", placeholder: "Diário, semanal..." },
  { key: "insuranceEndorsement", label: "Averbação de seguro", placeholder: "SIM / NÃO" },
  { key: "carrier", label: "Transportadora" },
  { key: "dispatcher", label: "Despachante" },
  { key: "contract", label: "Contrato" },
  { key: "dossierNumber", label: "Nº dossiê" },
  { key: "segment", label: "Segmento" },
  { key: "radar", label: "Tipo de radar" },
  { key: "services", label: "Serviços" },
  { key: "marginText", label: "Margem (texto)", placeholder: "Ex.: 0.25% + ICMS" },
  { key: "financialCost", label: "Custo financeiro", placeholder: "Ex.: CDI + 0.5%" },
];

const pick = (client: CommercialClient): IntranetClientFields => ({
  modality: client.modality ?? "",
  particularities: client.particularities ?? "",
  followUpFrequency: client.followUpFrequency ?? "",
  carrier: client.carrier ?? "",
  services: client.services ?? "",
  // Um por linha na tela; pipe no banco
  operationalEmails: (client.operationalEmails ?? "").split("|").filter(Boolean).join("\n"),
  analyst: client.analyst ?? "",
  dispatcher: client.dispatcher ?? "",
  segment: client.segment ?? "",
  radar: client.radar ?? "",
  contract: client.contract ?? "",
  dossierNumber: client.dossierNumber ?? "",
  insuranceEndorsement: client.insuranceEndorsement ?? "",
  internationalFreight: client.internationalFreight ?? "",
  marginText: client.marginText ?? "",
  financialCost: client.financialCost ?? "",
});

const sameFields = (a: IntranetClientFields, b: IntranetClientFields) => JSON.stringify(a) === JSON.stringify(b);

const percent = (value?: number | null) =>
  value == null ? "—" : `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const dateText = (value?: string | null) => (value ? new Date(value).toLocaleDateString("pt-BR") : "");

const ClientFichaDrawer = ({ clientId, onClose, onSaved }: Props) => {
  const [client, setClient] = useState<CommercialClient | null>(null);
  const [fields, setFields] = useState<IntranetClientFields>({});
  const [saved, setSaved] = useState<IntranetClientFields>({});
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [proceduresDirty, setProceduresDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const keepRef = useRef<HTMLButtonElement>(null);
  // Resposta atrasada de outra ficha não pode sobrescrever a aberta
  const currentId = useRef<string | null>(null);
  const toast = useToast();

  /** null = outra ficha foi aberta no meio. */
  const fetchFicha = async (id: string) => {
    const full = await getIntranetClient(id);
    return currentId.current === id ? full : null;
  };

  const load = async (id: string) => {
    try {
      const full = await fetchFicha(id);
      if (!full) return;
      setClient(full);
      setFields(pick(full));
      setSaved(pick(full));
    } catch {
      if (currentId.current !== id) return;
      toast({ title: "Não foi possível abrir a ficha.", status: "error" });
      onClose();
    }
  };

  useEffect(() => {
    currentId.current = clientId;
    setClient(null);
    setComment("");
    setProceduresDirty(false);
    if (clientId) load(clientId);
  }, [clientId]);

  const fieldsDirty = JSON.stringify(fields) !== JSON.stringify(saved);
  const requestClose = () => (fieldsDirty || proceduresDirty || comment.trim() ? setConfirmClose(true) : onClose());

  const save = async () => {
    if (!client?.id) return;
    const id = client.id;
    const submitted = fields;
    const payload = { ...submitted };
    // Também editados no Clientes Ativos: só vão no PUT se mudaram aqui (null = servidor mantém o atual)
    SHARED_INTRANET_FIELDS.forEach((key) => {
      if (submitted[key] === saved[key]) payload[key] = null;
    });
    setSaving(true);
    try {
      const { data } = await updateIntranetClient(id, payload);
      if (data && data.success === false) {
        toast({ title: data.notifications?.[0] ?? "Não foi possível salvar a ficha.", status: "error" });
        setSaving(false);
        return;
      }
    } catch {
      toast({ title: "Não foi possível salvar a ficha.", status: "error" });
      setSaving(false);
      return;
    }
    toast({ title: "Ficha atualizada.", status: "success" });
    onSaved();
    try {
      const full = await fetchFicha(id);
      if (!full) return;
      setClient(full);
      setSaved(pick(full));
      // O que foi digitado enquanto salvava continua na tela (e conta como alteração)
      setFields((current) => (sameFields(current, submitted) ? pick(full) : current));
    } catch {
      if (currentId.current !== id) return;
      setSaved(submitted);
      toast({ title: "Ficha salva, mas não foi possível recarregar.", status: "warning" });
    } finally {
      setSaving(false);
    }
  };

  const saveComment = async () => {
    if (!client?.id || !comment.trim()) return;
    const id = client.id;
    try {
      const { data } = await addIntranetClientComment(id, comment);
      if (data && data.success === false) {
        toast({ title: data.notifications?.[0] ?? "Não foi possível registrar o comentário.", status: "error" });
        return;
      }
    } catch {
      toast({ title: "Não foi possível registrar o comentário.", status: "error" });
      return;
    }
    // Já gravou: limpa antes de recarregar, senão falha no reload faz a pessoa postar de novo
    if (currentId.current === id) setComment("");
    try {
      const full = await fetchFicha(id);
      if (full) setClient(full);
    } catch {
      if (currentId.current === id) toast({ title: "Comentário registrado; reabra a ficha para vê-lo.", status: "warning" });
    }
  };

  const set = (key: keyof IntranetClientFields, value: string) => setFields((current) => ({ ...current, [key]: value }));

  return (
    <>
      <Drawer isOpen={clientId != null} placement="right" onClose={requestClose} size="full">
        <DrawerOverlay />
        <DrawerContent bg={crm.surface} color={crm.text} w="100%" maxW={{ base: "100%", md: "760px" }} ml="auto">
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px solid" borderColor={crm.border} pr="55px">
            <Text fontSize="xs" color={crm.textMuted} mb="6px">FICHA DO CLIENTE</Text>
            <Heading size="lg">{client?.name ?? ""}</Heading>
            {client && (
              <Flex gap="22px" mt="14px" wrap="wrap">
                <Metric label="CNPJ" value={client.cnpj || "—"} />
                <Metric label="Empresa" value={companyTypeLabel(client.companyType) || "—"} />
                <Metric label="Margem" value={percent(client.negotiatedMarginPercent)} />
                <Metric label="Status" value={client.status ?? "—"} />
              </Flex>
            )}
          </DrawerHeader>
          <DrawerBody pb="40px">
            {!client && <Flex py="60px" justify="center"><Spinner size="lg" /></Flex>}
            {client && (
              <>
                <Box mt="18px">
                  <Text fontSize="sm" color={crm.textMuted} mb="8px">Propriedades</Text>
                  <Box border="1px solid" borderColor={crm.border} borderRadius={crm.radius} overflow="hidden">
                    {FIELDS.map((field) => (
                      <Row key={field.key} label={field.label}>
                        <Input size="sm" variant="unstyled" px="2px" value={(fields[field.key] as string) ?? ""} placeholder={field.placeholder ?? "Vazio"} onChange={(e) => set(field.key, e.target.value)} />
                      </Row>
                    ))}
                    <Row label="E-mails" hint="Um por linha">
                      <Textarea size="sm" variant="unstyled" px="2px" rows={Math.max(2, (fields.operationalEmails ?? "").split("\n").length)} value={fields.operationalEmails ?? ""} placeholder="Vazio" onChange={(e) => set("operationalEmails", e.target.value)} />
                    </Row>
                    <Row label="Particularidades" last>
                      <Textarea size="sm" variant="unstyled" px="2px" rows={4} value={fields.particularities ?? ""} placeholder="Regras específicas deste cliente..." onChange={(e) => set("particularities", e.target.value)} />
                    </Row>
                  </Box>
                  <Flex mt="12px" gap="10px" align="center">
                    <Button colorScheme="blue" size="sm" onClick={save} isLoading={saving} isDisabled={!fieldsDirty}>Salvar ficha</Button>
                    {fieldsDirty && <Badge colorScheme="orange">Alterações não salvas</Badge>}
                  </Flex>
                </Box>

                <Box mt="30px" pt="20px" borderTop="1px solid" borderColor={crm.border}>
                  <ProceduresPanel scope={{ commercialClientId: client.id! }} onDirtyChange={setProceduresDirty} emptyText="Nenhum procedimento. Crie um para registrar instruções de embarque, faturamento etc." />
                </Box>

                <Box mt="30px" pt="20px" borderTop="1px solid" borderColor={crm.border}>
                  <Flex align="center" gap="8px"><FaComment /><Heading size="sm">Comentários</Heading></Flex>
                  {(client.comments ?? []).map((item) => (
                    <Box key={item.id} mt="14px">
                      <Text fontSize="sm" fontWeight="700">{item.authorName ?? "Equipe"} <Text as="span" fontWeight="400" color={crm.textMuted}>{dateText(item.createdDate)}</Text></Text>
                      <Text mt="4px" fontSize="sm" whiteSpace="pre-wrap">{item.text}</Text>
                    </Box>
                  ))}
                  <Textarea mt="16px" size="sm" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Adicionar comentário..." />
                  <Button mt="8px" size="sm" colorScheme="blue" onClick={saveComment} isDisabled={!comment.trim()}>Comentar</Button>
                </Box>
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <AlertDialog isOpen={confirmClose} leastDestructiveRef={keepRef} onClose={() => setConfirmClose(false)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Descartar alterações?</AlertDialogHeader>
            <AlertDialogBody>Há alterações não salvas nesta ficha. Se fechar agora, elas serão perdidas.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={keepRef} variant="ghost" onClick={() => setConfirmClose(false)}>Continuar editando</Button>
              <Button colorScheme="red" ml={3} onClick={() => { setConfirmClose(false); onClose(); }}>Descartar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <Box><Text color={crm.textMuted} fontSize="xs">{label}</Text><Text fontSize="sm" mt="3px">{value}</Text></Box>
);

const Row = ({ label, hint, last, children }: { label: string; hint?: string; last?: boolean; children: ReactNode }) => (
  <Grid templateColumns={{ base: "1fr", sm: "200px 1fr" }} borderBottom={last ? undefined : "1px solid"} borderColor={crm.border} _hover={{ bg: crm.subtle }} _focusWithin={{ bg: crm.brandSoft }}>
    <Box px="12px" pt="8px" pb={{ base: 0, sm: "8px" }}>
      <Text fontSize="sm" color={crm.textMuted}>{label}</Text>
      {hint && <Text fontSize="xs" color={crm.textFaint}>{hint}</Text>}
    </Box>
    <Box px="10px" py="6px">{children}</Box>
  </Grid>
);

export default ClientFichaDrawer;
